import React from 'react';
import './Toolbar.css';

export type Tool = 'pencil' | 'brush' | 'eraser' | 'fill' | 'select' | 'move' | 'copy' | 'eyedropper';

interface Props {
  tool: Tool;
  setTool: (t: Tool) => void;
  width: number;
  setWidth: (n: number) => void;
  cellAspect?: 'square'|'tall'|'long';
  setCellAspect?: (s: 'square'|'tall'|'long') => void;
  onExportCSV: () => void;
  onImportCSV: (f: File) => void;
  onExportImage?: () => void;
  onOpenExportOptions?: () => void;
  onTrim: () => void;
  onDeleteSelection: () => void;
  onCopySelection: () => void;
  onPasteClipboard: () => void;
  onFlipHorizontal: () => void;
  onFlipVertical: () => void;
  onStartCopyTool: () => void;
  onOpenImageImport: () => void;
  dark: boolean;
  toggleDark: () => void;
  showGrid?: boolean;
  toggleShowGrid?: () => void;
}

export default function Toolbar(props: Props) {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  return (
    <div className="toolbar" style={{display:'flex',alignItems:'center',gap:12}}>
      <div className="toolbar-left" style={{display:'flex',flexDirection:'column',gap:8}}>
        <div className="tools" style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
          <i
            role="button"
            tabIndex={0}
            onClick={() => props.setTool('pencil')}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.setTool('pencil'); } }}
            className={`fa-solid fa-pencil toolbar-icon ${props.tool === 'pencil' ? 'active' : ''}`}
            title="Pencil"
          />
          <i
            role="button"
            tabIndex={0}
            onClick={() => props.setTool('brush')}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.setTool('brush'); } }}
            className={`fa-solid fa-paint-brush toolbar-icon ${props.tool === 'brush' ? 'active' : ''}`}
            title="Brush"
          />
          <i
            role="button"
            tabIndex={0}
            onClick={() => props.setTool('fill')}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.setTool('fill'); } }}
            className={`fa-solid fa-fill-drip toolbar-icon ${props.tool === 'fill' ? 'active' : ''}`}
            title="Fill"
          />
          <i
            role="button"
            tabIndex={0}
            onClick={() => props.setTool('eraser')}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.setTool('eraser'); } }}
            className={`fa-solid fa-eraser toolbar-icon ${props.tool === 'eraser' ? 'active' : ''}`}
            title="Eraser"
          />
          <span style={{display:'inline-flex',alignItems:'center',paddingLeft:6,paddingRight:6}}>
            <label style={{display:'flex',alignItems:'center',gap:8,margin:0}}>Width:
              <input style={{verticalAlign:'middle'}} type="range" min={0} max={6} value={props.width} onChange={e => props.setWidth(parseInt(e.target.value || '0'))} />
            </label>
          </span>
          <i
            role="button"
            tabIndex={0}
            onClick={() => props.setTool('select')}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.setTool('select'); } }}
            className={`fa-regular fa-object-group toolbar-icon ${props.tool === 'select' ? 'active' : ''}`}
            title="Select"
          />
          <i
            role="button"
            tabIndex={0}
            onClick={() => props.setTool('move')}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.setTool('move'); } }}
            className={`fa-solid fa-arrows-up-down-left-right toolbar-icon ${props.tool === 'move' ? 'active' : ''}`}
            title="Move"
          />
          <i
            role="button"
            tabIndex={0}
            onClick={() => props.setTool('eyedropper')}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.setTool('eyedropper'); } }}
            className={`fa-solid fa-eye-dropper toolbar-icon ${props.tool === 'eyedropper' ? 'active' : ''}`}
            title="Eyedropper"
          />
          <i
            role="button"
            tabIndex={0}
            onClick={() => { props.setTool('copy'); props.onStartCopyTool(); }}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.setTool('copy'); props.onStartCopyTool(); } }}
            className={`fa-solid fa-copy toolbar-icon ${props.tool === 'copy' ? 'active' : ''}`}
            title="Copy"
          />
          <i
            role="button"
            tabIndex={0}
            onClick={props.onFlipHorizontal}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.onFlipHorizontal(); } }}
            className="fa-solid fa-arrows-left-right toolbar-icon"
            title="Flip Horizontal"
          />
          <i
            role="button"
            tabIndex={0}
            onClick={props.onFlipVertical}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.onFlipVertical(); } }}
            className="fa-solid fa-arrows-up-down toolbar-icon"
            title="Flip Vertical"
          />
        </div>
      </div>

      <div className="toolbar-center" style={{marginLeft:'auto',marginRight:'auto',display:'flex',gap:8,alignItems:'center'}}>
        <i
          role="button"
          tabIndex={0}
          onClick={props.onOpenImageImport}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.onOpenImageImport(); } }}
          className="fa-solid fa-file-image toolbar-icon"
          title="Import Image"
        />
        <i
          role="button"
          tabIndex={0}
          onClick={props.onExportCSV}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.onExportCSV(); } }}
          className="fa-solid fa-floppy-disk toolbar-icon"
          title="Export CSV"
        />
        <i
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
          className="fa-solid fa-folder-open toolbar-icon"
          title="Import CSV"
        />
        <input ref={fileInputRef} type="file" accept=".pattern,.csv,text/csv,text/plain" style={{display:'none'}} onChange={e => { if(e.target.files && e.target.files[0]) props.onImportCSV(e.target.files[0]); e.currentTarget.value=''; }} />
        <i
          role="button"
          tabIndex={0}
          onClick={() => { if(props.onOpenExportOptions) props.onOpenExportOptions(); else if(props.onExportImage) props.onExportImage(); }}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if(props.onOpenExportOptions) props.onOpenExportOptions(); else if(props.onExportImage) props.onExportImage(); } }}
          className="fa-solid fa-image toolbar-icon"
          title="Export Image"
        />
        <i
          role="button"
          tabIndex={0}
          onClick={props.onTrim}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.onTrim(); } }}
          className="fa-solid fa-scissors toolbar-icon"
          title="Re-zero & Trim"
        />
      </div>

      <div className="toolbar-right" style={{marginLeft:'auto',display:'flex',alignItems:'center'}}>
        
        <span style={{display:'inline-flex',alignItems:'center',gap:8,marginLeft:8}} title="Cell Aspect">
          <button
            type="button"
            aria-label="Toggle cell aspect"
            title={`Cell Aspect: ${props.cellAspect || 'square'} (click to cycle)`}
            onClick={() => {
              if(!props.setCellAspect) return;
              const next = (props.cellAspect === 'square') ? 'tall' : (props.cellAspect === 'tall') ? 'long' : 'square';
              props.setCellAspect(next);
            }}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if(props.setCellAspect){ const next = (props.cellAspect === 'square') ? 'tall' : (props.cellAspect === 'tall') ? 'long' : 'square'; props.setCellAspect(next); } } }}
            style={{background:'transparent',border:'none',display:'inline-flex',alignItems:'center',cursor:'pointer'}}
            className="aspect-toggle"
          >
            <svg width="28" height="28" viewBox="0 0 30 34" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="true">
              {props.cellAspect === 'tall' ? (
                // standing rectangle: 20x30 centered
                <rect x="5" y="2" width="20" height="30" rx="2" ry="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
              ) : props.cellAspect === 'long' ? (
                // lying rectangle: 30x20 centered vertically
                <rect x="0" y="7" width="30" height="20" rx="2" ry="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
              ) : (
                // square: 20x20 centered
                <rect x="5" y="7" width="20" height="20" rx="2" ry="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
              )}
            </svg>
          </button>
        </span>
        <span style={{display:'inline-flex',alignItems:'center',gap:8,marginLeft:8}} title="Toggle Grid Lines">
          <i
            role="button"
            tabIndex={0}
            onClick={() => props.toggleShowGrid && props.toggleShowGrid()}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.toggleShowGrid && props.toggleShowGrid(); } }}
            style={{marginRight: '10px'}}
            className={`fa-solid fa-border-all toolbar-icon ${props.showGrid ? 'active' : ''}`}
          />
        </span>
        <span style={{display:'inline-flex',alignItems:'center',gap:8}} title="Toggle Dark Mode">
          <i
            role="button"
            tabIndex={0}
            onClick={props.toggleDark}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.toggleDark(); } }}
            className={`fa-solid fa-${props.dark ? 'sun' : 'moon' } toolbar-icon`}
          />
          {/* <input type="checkbox" checked={props.dark} onChange={props.toggleDark} /> */}
        </span>
        
      </div>
    </div>
  );
}
